import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Menu, X, Droplet, Phone, Mail, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [formStatus, setFormStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: "", message: "" });

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setFormStatus({ 
        type: "success", 
        message: response.data.message 
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      setFormStatus({ 
        type: "error", 
        message: "Failed to send message. Please try again or call us directly." 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white relative">
      {/* Fixed Large Logo Background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none flex items-center justify-center"
        style={{
          backgroundImage: 'url(/logo-watermark.png)',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '70%',
          opacity: '0.15'
        }}
      ></div>

      {/* Content Container */}
      <div className="relative z-10">
        {/* Header/Navigation */}
        <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50 shadow-sm">
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              {/* Logo and Company Name */}
              <div className="flex items-center space-x-4">
                <img 
                  src="/logo.png" 
                  alt="Irrigation Specialists of Northern Colorado" 
                  className="h-16 w-auto"
                />
                <div className="block text-center">
                  <h1 className="text-base md:text-lg font-bold text-gray-900 tracking-wide leading-tight">
                    IRRIGATION SPECIALISTS
                  </h1>
                  <p className="text-xs md:text-sm text-cyan-600 font-semibold">
                    OF NORTHERN COLORADO
                  </p>
                </div>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden md:flex space-x-8">
                <button
                  onClick={() => scrollToSection("services")}
                  className="text-gray-700 hover:text-cyan-600 font-medium transition"
                >
                  Services
                </button>
                <button
                  onClick={() => scrollToSection("about")}
                  className="text-gray-700 hover:text-cyan-600 font-medium transition"
                >
                  About Us
                </button>
                <button
                  onClick={() => scrollToSection("contact")}
                  className="text-gray-700 hover:text-cyan-600 font-medium transition"
                >
                  Contact
                </button>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden text-gray-700 hover:text-cyan-600"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>

            {/* Mobile Navigation */}
            {isMenuOpen && (
              <div className="md:hidden py-4 border-t border-gray-200">
                <div className="flex flex-col space-y-3">
                  <button
                    onClick={() => scrollToSection("services")}
                    className="text-left text-gray-700 hover:text-cyan-600 font-medium py-2"
                  >
                    Services
                  </button>
                  <button
                    onClick={() => scrollToSection("about")}
                    className="text-left text-gray-700 hover:text-cyan-600 font-medium py-2"
                  >
                    About Us
                  </button>
                  <button
                    onClick={() => scrollToSection("contact")}
                    className="text-left text-gray-700 hover:text-cyan-600 font-medium py-2"
                  >
                    Contact
                  </button>
                </div>
              </div>
            )}
          </nav>
        </header>

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto text-center">
            <div className="inline-block mb-6">
              <img 
                src="/logo.png" 
                alt="Irrigation Specialists of Northern Colorado" 
                className="h-32 w-auto mx-auto"
              />
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">
                Responsible Water Use
              </span>
              <br />
              Starts Below The Surface
            </h2>
            <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
              Professional irrigation solutions for Northern Colorado. Providing reliable service focused on efficient irrigation, system performance, and responsible water use.
            </p>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-cyan-600 to-blue-600 mx-auto mb-4"></div>
              <p className="text-xl text-gray-600">Comprehensive Irrigation Maintenance & Repair Services</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Complete Irrigation System Diagnostics & Repair",
                  description: "Comprehensive evaluation and repair of irrigation systems, including water flow, valves, controllers, wiring, and overall system performance."
                },
                {
                  title: "Certified Backflow Preventer Testing & Reporting",
                  description: "Professional testing, certification, and required reporting for residential and commercial irrigation systems and building backflow assemblies."
                },
                {
                  title: "Scheduled Maintenance Programs",
                  description: "Proactive, recurring service plans designed to keep systems operating efficiently, reduce costly repairs, and extend system lifespan."
                },
                {
                  title: "Irrigation Audits",
                  description: "Detailed system audits to identify inefficiencies, improve water distribution, and recommend upgrades that reduce water use while maintaining healthy landscapes."
                },
                {
                  title: "Consultation Services",
                  description: "Expert guidance on system design improvements, technology upgrades, water conservation strategies, and long-term irrigation planning."
                },
                {
                  title: "Water Emergency Response",
                  description: "Rapid response to leaks, breaks, and system failures to minimize water loss, property damage, and downtime."
                }
              ].map((service, index) => (
                <div
                  key={index}
                  className="p-6 bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl shadow-md hover:shadow-xl hover:shadow-cyan-500/10 transition transform hover:scale-105 hover:border-cyan-500/50"
                  data-testid={`service-card-${index}`}
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Us Section */}
        <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-gray-50 to-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">About Us</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-cyan-600 to-blue-600 mx-auto mb-8"></div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-8 md:p-12">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Irrigation Specialists of Northern Colorado is a trusted provider of professional irrigation maintenance, repair, and system optimization services throughout Northern Colorado. With over 25 years of experience and a strong commitment to responsible water use, we deliver reliable service grounded in proven, sustainable irrigation practices.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our team of certified professionals supports both residential and commercial irrigation systems. We understand Colorado's unique climate challenges and focus on maintaining, repairing, and upgrading systems to operate efficiently while minimizing water waste.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Equally important, we believe informed clients make better long-term decisions. We take the time to explain how irrigation systems function, identify current issues, and highlight potential risks that could lead to future problems. Through routine maintenance, technology upgrades, and emergency repairs, we provide precise, dependable care while helping clients understand, manage, and protect their irrigation investment.
              </p>

              <div className="mt-12 grid md:grid-cols-3 gap-8 text-center">
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-6 border border-cyan-200">
                  <div className="text-4xl font-bold text-cyan-600 mb-2">25+</div>
                  <div className="text-gray-700">Years Experience</div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 border border-blue-200">
                  <div className="text-4xl font-bold text-blue-600 mb-2">1000+</div>
                  <div className="text-gray-700">Systems Serviced</div>
                </div>
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-6 border border-cyan-200">
                  <div className="text-4xl font-bold text-cyan-600 mb-2">100%</div>
                  <div className="text-gray-700">Client Focused</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-cyan-600 to-blue-600 mx-auto mb-4"></div>
              <p className="text-xl text-gray-600">Professional Irrigation Support Starts Here</p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Get In Touch</h3>
                  <p className="text-gray-600 mb-8">
                    Have questions about our services or need immediate assistance? 
                    We're here to help!
                  </p>
                </div>

                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Phone</h4>
                    <a 
                      href="tel:9705932609" 
                      className="text-cyan-600 hover:text-cyan-700 text-lg font-medium transition"
                      data-testid="contact-phone-link"
                    >
                      970.593.2609
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Email</h4>
                    <a 
                      href="mailto:Contact@ISNoCO.com" 
                      className="text-cyan-600 hover:text-cyan-700 text-lg font-medium transition"
                      data-testid="contact-email-link"
                    >
                      Contact@ISNoCO.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
                
                {formStatus.message && (
                  <div 
                    className={`mb-6 p-4 rounded-lg border ${
                      formStatus.type === "success" 
                        ? "bg-green-50 text-green-800 border-green-300" 
                        : "bg-red-50 text-red-800 border-red-300"
                    }`}
                    data-testid="form-status-message"
                  >
                    {formStatus.message}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      data-testid="contact-form-name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      data-testid="contact-form-email"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      data-testid="contact-form-phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows="4"
                      className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-900 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                      data-testid="contact-form-message"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold rounded-lg hover:shadow-xl hover:shadow-cyan-500/30 transform hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-form-submit"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <img 
                src="/logo.png" 
                alt="Irrigation Specialists of Northern Colorado" 
                className="h-16 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-gray-400 mb-4">Responsible Water Use Starts Below The Surface</p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-sm text-gray-400">
              <a href="tel:9705932609" className="hover:text-cyan-400 transition">970.593.2609</a>
              <span className="hidden sm:inline text-gray-600">|</span>
              <a href="mailto:Contact@ISNoCO.com" className="hover:text-cyan-400 transition">Contact@ISNoCO.com</a>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800">
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} Irrigation Specialists of Northern Colorado. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
